import React from 'react';
import {
  Box,
  Typography,
  Link,
  Paper,
  Divider,
  ListItem,
  List,
  ListItemText,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

function DataPolicyTable() {
  const rows = [
    {
      category: 'Account & Profile Data',
      examples: 'Name, email address, mobile number, password',
      purpose:
        'To create, manage, and secure your user account; to communicate essential service-related information.',
      basis: 'Consent',
      retention:
        'Retained for as long as your account is active, or until you request erasure.',
    },
    {
      category: 'Astrological Data',
      examples: 'Date of birth, time of birth, city/country of birth',
      purpose:
        'To generate personalized astrological charts, reports, and AI-driven predictions as the core function of the Services.',
      basis: 'Consent',
      retention:
        'Retained for as long as your account is active to provide ongoing services, or until you request erasure.',
    },
    {
      category: 'AI Interaction Data',
      examples: 'Text prompts, questions, and conversations with the AI',
      purpose:
        'To provide real-time, context-aware responses and astrological guidance.',
      basis: 'Consent',
      retention:
        'Conversation history is retained to provide a continuous user experience, subject to your right to erasure.',
    },
    {
      category: 'Payment Data',
      examples:
        'Transaction ID, payment status, amount paid (Full financial details are not stored by us)',
      purpose:
        'To confirm and process your orders for premium services; for financial auditing and legal compliance.',
      basis:
        'Processing is necessary for the performance of a contract (fulfilling your purchase).',
      retention:
        'Retained for the period required by applicable financial and tax laws (typically several years).',
    },
    {
      category: 'Communications Data',
      examples: 'Emails, support tickets, feedback submissions',
      purpose:
        'To respond to your inquiries, provide customer support, and improve our Services.',
      basis: 'Consent (implied by your initiation of contact).',
      retention:
        'Retained for a reasonable period to ensure resolution of the issue and for quality assurance purposes.',
    },
    {
      category: 'Usage & Log Data',
      examples:
        'IP address, browser type, operating system, pages viewed, access times',
      purpose:
        'To monitor and analyze service performance, ensure security, prevent fraud, and diagnose technical issues.',
      basis:
        'Legitimate Uses (for ensuring the security and integrity of the platform).',
      retention:
        'Retained for a limited period necessary for security analysis and then aggregated or anonymized.',
    },
    {
      category: 'Device Information',
      examples: 'Hardware model, OS version, unique device identifiers',
      purpose:
        'To optimize the service for different devices and troubleshoot platform-specific bugs.',
      basis:
        'Legitimate Uses (for ensuring the proper functioning and accessibility of the service).',
      retention:
        'Retained for as long as necessary to provide device-specific support and optimization.',
    },
    {
      category: 'Cookies',
      examples: 'Session identifiers, user preferences',
      purpose:
        'To maintain user sessions, remember preferences, and facilitate a seamless user experience.',
      basis: 'Consent (obtained via a cookie banner or settings).',
      retention:
        'Varies by cookie type (session cookies expire upon browser closure; persistent cookies have a set expiration date).',
    },
  ];

  return (
    <TableContainer
      component={Paper}
      sx={{
        mt: 3,
        border: '1px solid #e0e0e0',
        padding: 0.25,
        boxShadow: 'none',
      }}
    >
      <Table
        sx={{
          '& .MuiTableCell-root': {
            border: 'none',
            fontSize: '12px',
            verticalAlign: 'top',
            textAlign: 'left',
          },
        }}
      >
        <TableHead>
          <TableRow>
            {[
              'Category of Personal Data',
              'Specific Examples',
              'Primary Purpose of Processing',
              'Legal Basis (under DPDPA)',
              'Data Retention Period',
            ].map((header) => (
              <TableCell key={header}>
                <b>{header}</b>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.category}</TableCell>
              <TableCell>{row.examples}</TableCell>
              <TableCell>{row.purpose}</TableCell>
              <TableCell>{row.basis}</TableCell>
              <TableCell>{row.retention}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const PrivacyPolicy = () => {
  return (
    <Box>
      <Typography variant="h2">1. Our Commitment to Your Privacy</Typography>
      <Typography variant="homePageBody" paragraph>
        Welcome to <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>
        . Our mission is to provide personalized, insightful astrological
        guidance through the power of artificial intelligence. We understand
        that the journey of self-discovery and spiritual exploration you
        undertake with our Services is deeply personal. Therefore, our
        commitment to protecting your privacy and safeguarding your personal
        data is a foundational tenet of our platform. This Privacy Policy is
        designed to be a transparent and comprehensive guide to our data
        practices, ensuring you are fully informed and in control of your
        information.
      </Typography>

      <Typography variant="homePageBody" paragraph>
        This document outlines the types of Personal Data we collect, the
        purposes for which we process it, the measures we take to protect it,
        and the rights you possess regarding your data. Your use of the
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> website
        (https://www.kaliyugai.com/), its associated applications, and services
        (collectively, the "Services") signifies your understanding and
        unconditional consent to the terms laid out in this Privacy Policy. This
        policy should be read in conjunction with our Legal Terms  
      </Typography>

      <Typography variant="homePageBody" paragraph>
        The name "<span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>"
        reflects a period of profound challenge and significant opportunity for
        spiritual growth. In this spirit, we view our role not merely as a
        service provider, but as a trusted custodian of the sensitive
        information you share. We believe that true guidance can only be offered
        in an environment of absolute trust, and this policy is our pledge to
        uphold that trust.  
      </Typography>

      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        1.1. Definitions
      </Typography>
      <Typography variant="homePageBody" paragraph>
        To ensure clarity, the following terms used throughout this policy have
        specific meanings, aligned with India's Digital Personal Data Protection
        Act, 2023 (DPDPA):
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Personal Data:
                  </Typography>{' '}
                  Means any data about an individual who is identifiable by or
                  in relation to such data. This includes information you
                  provide directly, such as your name and birth details, as well
                  as information collected automatically, like your IP address.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Data Principal:
                  </Typography>{' '}
                  Refers to you, the individual to whom the Personal Data
                  relates. In this policy, "you" and "your" refer to the Data
                  Principal.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Data Fiduciary:
                  </Typography>{' '}
                  Refers to the entity that, alone or in conjunction with
                  others, determines the purposes and means of the processing of
                  Personal Data. For the purposes of this policy,{' '}
                  <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>{' '}
                  is the Data Fiduciary.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Data Processor:
                  </Typography>{' '}
                  Refers to any person who processes Personal Data on behalf of
                  a Data Fiduciary.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Processing:
                  </Typography>{' '}
                  In relation to Personal Data, means an automated operation or
                  set of operations performed on digital Personal Data, such as
                  collection, recording, organization, structuring, storage,
                  adaptation, retrieval, use, alignment or combination,
                  indexing, sharing, disclosure, dissemination, making
                  available, restriction, erasure, or destruction.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Consent:
                  </Typography>{' '}
                  Means any free, specific, informed, unconditional, and
                  unambiguous indication of the Data Principal’s wishes by which
                  they, through a clear affirmative action, signify agreement to
                  the processing of their Personal Data for a specified purpose.
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        2. The Information We Collect and Why
      </Typography>
      <Typography variant="homePageBody" paragraph>
        To provide you with accurate, personalized, and meaningful astrological
        insights, we must collect and process certain Personal Data. The
        principle of "purpose limitation" under the DPDPA guides our data
        collection practices; we only collect data that is necessary for the
        specific, explicit, and legitimate purposes outlined below. We are
        transparent about what we collect and why, ensuring you are always
        informed.  
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        2.1. Information You Voluntarily Provide
      </Typography>
      <Typography variant="homePageBody" paragraph>
        This is data you actively and knowingly share with us when you interact
        with our Services.
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span">
                    Account and Profile Data:
                  </Typography>{' '}
                  When you register for an account, we collect essential
                  information to create and secure your profile. This typically
                  includes your full name, email address, a secure password and
                  for verification purposes such as through a One-Time Password
                  (OTP). This information is necessary to identify you as a
                  user, secure your account, and provide you with access to our
                  Services.
                </>
              }
            />
          </ListItem>

          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span">Astrological Data:</Typography>{' '}
                  This is the most critical information required for the core
                  functionality of our Services. To generate your natal chart
                  and provide personalised astrological readings, we require
                  your:
                  <List sx={{ listStyleType: 'circle', pl: 2 }} disablePadding>
                    <ListItem
                      sx={{ display: 'list-item' }}
                      disableGutters
                      disablePadding
                    >
                      <ListItemText primary="Date of birth" />
                    </ListItem>
                    <ListItem
                      sx={{ display: 'list-item' }}
                      disableGutters
                      disablePadding
                    >
                      <ListItemText primary="Exact time of birth" />
                    </ListItem>
                    <ListItem
                      sx={{ display: 'list-item' }}
                      disableGutters
                      disablePadding
                    >
                      <ListItemText primary="City, state, and country of birth" />
                    </ListItem>
                  </List>
                  This data is considered highly sensitive, and we treat it with
                  the utmost confidentiality and security. Its collection is
                  solely for the purpose of delivering the astrological
                  calculations and interpretations you request.
                </>
              }
            />
          </ListItem>

          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span">AI Interaction Data:</Typography>{' '}
                  This category includes all the prompts, questions, and
                  conversational text you input when interacting with our AI
                  System. These interactions are the primary method through
                  which you receive astrological guidance. This data is
                  processed in real-time to generate AI-powered responses
                  tailored to your queries. By default, the content of these
                  conversations is used solely to provide you with the immediate
                  service requested. The use of this data for any secondary
                  purpose, such as improving our AI models, is subject to our
                  explicit control, as detailed in Section 5.4 of this policy.
                </>
              }
            />
          </ListItem>

          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span">Payment Information:</Typography>{' '}
                  To access our premium services, you will be required to make a
                  payment. We utilize Razorpay and other Platforms, secure and
                  compliant third-party payment gateways, to process all
                  transactions. When you proceed to payment, you are providing
                  your financial information such as credit card number, debit
                  card number, or bank account details directly to Razorpay or
                  other Platforms' secure environment.{' '}
                  <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>{' '}
                  does not collect, store, or have access to your full financial
                  details. Our systems only receive transaction confirmation
                  data from Razorpay, such as a unique transaction ID, the
                  amount paid, and the status of the transaction (success or
                  failure), which is necessary for us to activate your purchased
                  services and for our financial record-keeping.
                </>
              }
            />
          </ListItem>

          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span">Communications Data:</Typography>{' '}
                  If you contact us directly for customer support, to provide
                  feedback, or for any other inquiry, we will collect the
                  information you include in your communication, such as your
                  email address, the content of your message, and any
                  attachments you provide. This data is used exclusively to
                  address your query and improve our customer service.
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        2.2. Information Collected Automatically
      </Typography>
      <Typography variant="homePageBody" paragraph>
        When you access and use our Services, we automatically collect certain
        technical information to ensure the proper functioning, security, and
        optimization of our platform.
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Usage and Log Data:
                  </Typography>{' '}
                  We automatically record information about your interactions
                  with our Services. This includes your Internet Protocol (IP)
                  address on our AWS server, the type of browser and operating
                  system you use, the dates and times of your access, the pages
                  you view, the time spent on those pages, and the referring URL
                  (the webpage that led you to our site). This information is
                  crucial for administering the service, diagnosing technical
                  problems, preventing fraudulent activity, and analysing user
                  trends to improve our platform.  
                </>
              }
            />
          </ListItem>

          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Device Information:
                  </Typography>{' '}
                  We may collect information about the device you use to access
                  our Services, such as the hardware model, operating system
                  version, and unique device identifiers. This helps us optimize
                  the user experience for different devices and troubleshoot
                  device-specific issues.  
                </>
              }
            />
          </ListItem>

          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Cookies and Similar Technologies:
                  </Typography>{' '}
                  We use cookies, which are small text files stored on your
                  device, to operate and enhance our Services. We may use both
                  session cookies (which expire when you close your browser) and
                  persistent cookies (which remain on your device until
                  deleted). These technologies are used to:
                  <List sx={{ listStyleType: 'circle', pl: 2 }} disablePadding>
                    <ListItem
                      sx={{ display: 'list-item' }}
                      disableGutters
                      disablePadding
                    >
                      <ListItemText primary="Remember your login information so you do not have to re-enter it each time you visit." />
                    </ListItem>
                    <ListItem
                      sx={{ display: 'list-item' }}
                      disableGutters
                      disablePadding
                    >
                      <ListItemText primary="Maintain your session and preferences as you navigate the Services." />
                    </ListItem>
                    <ListItem
                      sx={{ display: 'list-item' }}
                      disableGutters
                      disablePadding
                    >
                      <ListItemText primary="Understand how you use our Services to personalise and improve your experience." />
                    </ListItem>
                    <ListItem
                      sx={{ display: 'list-item' }}
                      disableGutters
                      disablePadding
                    >
                      <ListItemText primary="Gather aggregate usage and web traffic routing information. You have the ability to accept or decline cookies through your browser settings. However, please note that disabling cookies may prevent you from fully experiencing all the features of our Services.  " />
                    </ListItem>
                  </List>
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        2.3. Summary of Data Processing Activities
      </Typography>
      <Typography variant="homePageBody" paragraph>
        The following table provides a clear, at-a-glance summary of our data
        processing activities, in compliance with our transparency obligations
        under the DPDPA.  
        <DataPolicyTable />
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        3. How We Use Your Personal Data
      </Typography>
      <Typography variant="homePageBody" paragraph>
        To provide you with accurate, personalized, and meaningful astrological
        insights, we must collect and process certain Personal Data. The
        principle of "purpose limitation" under the DPDPA guides our data
        collection practices; we only collect data that is necessary for the
        specific, explicit, and legitimate purposes outlined below. We are
        transparent about what we collect and why, ensuring you are always
        informed.  
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        3.1. To Operate and Personalize Your Astrological Experience
      </Typography>
      <Typography variant="homePageBody" paragraph>
        The primary use of your Personal Data, particularly your Astrological
        Data and AI Interaction Data, is to deliver the core functionality of{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>. We use
        your birth details to compute precise astrological charts (such as the
        natal chart) which form the foundation for all personalized readings.
        Your queries and conversations with our AI are then processed against
        this astrological backdrop to provide you with tailored insights,
        predictions, and guidance. This synthesis of your unique astrological
        profile with your specific inquiries allows us to offer a deeply
        personalized and relevant experience, which is the central value
        proposition of our Services.  
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        3.2. To Improve Our Artificial Intelligence Models
      </Typography>
      <Typography variant="homePageBody" paragraph>
        The advancement of artificial intelligence relies on learning from vast
        amounts of data. To enhance the accuracy, nuance, and capabilities of{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>, we may
        use AI Interaction Data for model training, debugging, and overall
        service improvement. However, we recognize the profound sensitivity of
        the conversations you have with our AI, which may touch upon personal
        and spiritual matters.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Therefore, our approach to using this data is grounded in privacy-first
        principles and user control. When data is used for this purpose, we
        employ technical measures, such as de-identification and aggregation, to
        remove direct personal identifiers and protect your anonymity. Most
        importantly, your participation in this improvement process is entirely
        your choice. We provide you with clear and accessible controls to
        opt-out of having your conversation data used for model training with
        compliance of Terms and Conditions. This critical choice is detailed in
        Section 5.4. This approach distinguishes{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> as a
        service that respects user privacy not as an afterthought, but as a core
        design principle.  
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        3.3. To Process Payments and Transactions
      </Typography>
      <Typography variant="homePageBody" paragraph>
        As described in Section 2.1, we use a dedicated third-party payment
        processor, Razorpay, to handle all financial transactions. The
        information we receive from Razorpay (such as transaction confirmation)
        is used to verify your payment, grant you access to the corresponding
        premium features or services you have purchased, maintain our financial
        records for accounting and tax purposes, and resolve any billing
        disputes that may arise.  
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        3.4. For Security, Safety, and Legal Compliance
      </Typography>
      <Typography variant="homePageBody" paragraph>
        We use automatically collected data, such as IP addresses, device
        information, and usage logs, to maintain the security and integrity of
        our Services. This includes activities such as:
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText primary="Preventing, detecting, and investigating fraudulent or unauthorized activity." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText primary="Protecting our systems from malicious attacks, such as Distributed Denial of Service (DDoS) attacks." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText primary="Enforcing our Terms of Service with respect to Legal Terms." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText primary="Complying with our legal obligations, such as responding to valid legal requests from law enforcement or government authorities." />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        3.5. To Communicate with You
      </Typography>
      <Typography variant="homePageBody" paragraph>
        We use your Account and Profile Data, specifically your email address
        and/or mobile number If necessary to communicate with you. These
        communications fall into two categories:
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Service-Related Communications:
                  </Typography>{' '}
                  These are essential for the operation of the Services and
                  include account verification emails, notifications about
                  changes to our Services or policies, security alerts, and
                  responses to your support requests. You cannot opt-out of
                  these communications as they are necessary for us to provide
                  the service to you securely.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Marketing and Promotional Communications:
                  </Typography>{' '}
                  We may, with explicit consent, send you newsletters, special
                  offers, or other information about products and services that
                  may be of interest to you. You have the right to opt-out of
                  receiving these communications at any time by following the
                  "unsubscribe" link provided in every email or by adjusting the
                  notification settings in your account profile.  
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        4. How We Share and Disclose Your Information
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Our policy is to not sell, rent, or lease your Personal Data to third
        parties for their marketing purposes. We share your information only in
        the limited circumstances described below, and always with a commitment
        to protecting your privacy.
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        4.1. With Third-Party Service Providers (Data Processors)
      </Typography>
      <Typography variant="homePageBody" paragraph>
        We engage trusted third-party companies and individuals to perform
        certain business functions on our behalf. These partners are our Data
        Processors and are contractually obligated to process your data only as
        instructed by us and to implement robust security measures to protect
        it, in full compliance with the DPDPA. Categories of these providers
        include:  
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Payment Processors:
                  </Typography>{' '}
                  As detailed previously, we share transaction information with
                  Razorpay to facilitate payments. The mechanics of this process
                  are designed for maximum security: you provide your sensitive
                  payment details directly into Razorpay’s secure system,
                  meaning{' '}
                  <span style={{ fontFamily: 'SpaceGrotesk' }}>
                    KaliyugAi's
                  </span>{' '}
                  servers never handle or store your full credit card number.
                  This adherence to standards like PCI-DSS minimizes risk and
                  provides you with strong assurances about the safety of your
                  financial data.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Cloud Hosting and Infrastructure Providers:
                  </Typography>{' '}
                  Our Services are hosted on secure servers provided by leading
                  cloud infrastructure companies i.e. AWS. Your data is stored
                  and processed within these secure environments.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Analytics Services:
                  </Typography>{' '}
                  We may use third-party analytics tools to help us understand
                  usage patterns for our Services. These tools collect
                  information sent by your device or our Services, and we use
                  this data in an aggregated form that cannot reasonably be used
                  to identify any particular individual user.
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        4.2. To Comply with the Law or Protect Our Rights
      </Typography>
      <Typography variant="homePageBody" paragraph>
        We may disclose your Personal Data if we believe in good faith that such
        disclosure is necessary to:
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText primary="Comply with a legal obligation, such as a court order, subpoena, or a request from law enforcement or other government agencies." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText
              primary={
                <>
                  Protect and defend the rights, property, or safety of{' '}
                  <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>,
                  our users, or the public.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText primary="Prevent or investigate possible wrongdoing in connection with the Services." />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }} disableGutters disablePadding>
            <ListItemText primary="Enforce our Terms of Service or other legal agreements." />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        4.3. In Connection with a Business Transfer
      </Typography>
      <Typography variant="homePageBody" paragraph>
        In the event that{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> is
        involved in a merger, acquisition, debt financing, sale of assets, or
        bankruptcy, your Personal Data may be sold or transferred as part of
        that transaction. Should this occur, we will notify you before your
        Personal Data is transferred and becomes subject to a different privacy
        policy. The acquiring entity will be required to honor the commitments
        we have made in this Privacy Policy.  
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        4.4. With Your Explicit Consent
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Except as described above, we may at our discretion, share your Personal
        Data with third parties when necessary for the provision of our
        services, to comply with legal obligations, or to protect our legitimate
        interests. In other cases, we will obtain your explicit, free, specific,
        informed, and unambiguous consent before sharing your Personal Data.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        5. Your Rights Over Your Data
      </Typography>
      <Typography variant="homePageBody" paragraph>
        In accordance with the DPDPA and our commitment to user empowerment, you
        have comprehensive rights over your Personal Data. We have designed our
        Services to make it straightforward for you to exercise these rights.
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        5.1. Right to Access and Receive Information
      </Typography>
      <Typography variant="homePageBody" paragraph>
        You may request confirmation of whether your Personal Data is being
        processed and access to such data. However, we reserve the right to
        verify your identity and assess requests before providing access, to
        ensure the security and integrity of our systems. We may limit or deny
        access if fulfilling the request could negatively impact the security,
        privacy of other users, or our legitimate business interests.
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        5.2. Right to Correction and Erasure
      </Typography>
      <Typography variant="homePageBody" paragraph>
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Correction:
                  </Typography>{' '}
                  You can update certain Personal Data through your account
                  settings. However, we are not obligated to accept changes that
                  conflict with our internal records or regulatory requirements.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Erasure ("Right to be Forgotten"):
                  </Typography>{' '}
                  You may request deletion of your Personal Data, but we will
                  retain data as necessary to comply with applicable laws,
                  resolve disputes, or enforce our terms of service. We will
                  evaluate each request and exercise our discretion in
                  determining whether full or partial deletion is appropriate.
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        5.3. Right to Withdraw Consent
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Where we process Personal Data based on your consent, you may withdraw
        that consent at any time. However, if such withdrawal prevents us from
        delivering essential Services, we reserve the right to suspend or
        terminate your access. Withdrawal does not impact the processing of data
        carried out prior to withdrawal.
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        5.4. Your Choices Regarding AI Model Training
      </Typography>
      <Typography variant="homePageBody" paragraph>
        While we offer an option to opt out of having your anonymized AI
        Interaction Data used for model training, we retain the right to process
        your data as necessary to provide our Services, maintain system
        security, and comply with legal obligations. Opting out means your data
        will not be used for model training, but will continue to be processed
        for service delivery purposes.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        6. Data Security and Retention
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        6.1. Data Security
      </Typography>
      <Typography variant="homePageBody" paragraph>
        We take the security of your Personal Data very seriously. We implement
        a range of technical, administrative, and organizational security
        measures designed to protect your data from loss, misuse, and
        unauthorized access, disclosure, alteration, or destruction. These
        measures include:  
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Encryption:
                  </Typography>{' '}
                  We use industry-standard Transport Layer Security (TLS/SSL) to
                  encrypt all data transmitted between your device and our
                  servers. Sensitive data, such as Astrological Data, is also
                  encrypted at rest in our databases.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Access Controls:
                  </Typography>{' '}
                  Access to Personal Data within our organization is strictly
                  limited to authorized employees and contractors who have a
                  legitimate business need to access it. All such personnel are
                  subject to strict confidentiality obligations.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Regular Security Audits:
                  </Typography>{' '}
                  We conduct regular security assessments and vulnerability
                  scans to identify and remediate potential threats to our
                  systems.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Secure Development Practices:
                  </Typography>{' '}
                  Our software is developed with security best practices in mind
                  to minimise vulnerabilities from the outset.
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="homePageBody" paragraph>
        While we take all reasonable precautions, no method of transmission over
        the Internet or method of electronic storage is 100% secure. Therefore,
        we cannot guarantee its absolute security
      </Typography>
      <Typography variant="h3" sx={{ marginTop: '80px' }}>
        6.2. Data Retention
      </Typography>
      <Typography variant="homePageBody" paragraph>
        We retain your Personal Data only for as long as is necessary to fulfill
        the purposes for which it was collected, including for the purposes of
        satisfying any legal, accounting, or reporting requirements. The
        retention period for different categories of data is determined by its
        nature and the purpose of its processing:  
        <List sx={{ listStyleType: 'disc', pl: 2 }} disablePadding>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Account and Astrological Data:
                  </Typography>{' '}
                  This data is retained for as long as your account remains
                  active, as it is necessary to provide you with ongoing
                  Services. If you delete your account, this data will be erased
                  in accordance with our erasure procedures.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Transactional Data:
                  </Typography>{' '}
                  Information related to payments is retained for a longer
                  period (e.g., 5 years) to comply with tax and financial
                  regulations.
                </>
              }
            />
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            <ListItemText
              primary={
                <>
                  <Typography component="span" fontWeight="bold">
                    Log Data:
                  </Typography>{' '}
                  Security and usage logs are retained for a limited period
                  sufficient for security analysis and troubleshooting, after
                  which they are either deleted or anonymized.
                </>
              }
            />
          </ListItem>
        </List>
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        7. Children’s Privacy
      </Typography>
      <Typography variant="homePageBody" paragraph>
        Our Services are intended exclusively for individuals who are at least
        18 years of age. We do not target, market to, or knowingly collect
        Personal Data from individuals under the age of 18 ("Children"). In
        accordance with the DPDPA, processing Personal Data of a Child requires
        verifiable consent from their parent or legal guardian.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        If we become aware that Personal Data from a Child has been collected
        without proper parental or guardian consent, we will take appropriate
        steps to remove such data from our systems at our discretion and as
        required by law. However, we do not undertake active verification of age
        or parental consent beyond the measures reasonably required by our
        Services.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        If you are a parent or guardian and believe your Child has submitted
        Personal Data to us, you should contact us immediately. We will assess
        the situation and proceed in line with our internal policies and legal
        obligations, but we reserve the right to determine the appropriate
        action based on our judgment.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        8. International Data Transfers
      </Typography>
      <Typography variant="homePageBody" paragraph>
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> operates
        on a global scale, and your Personal Data may be transferred to, stored,
        and processed in countries other than the one in which you reside. These
        countries may have data protection laws that differ from those of your
        country. As part of our business operations, we may exercise discretion
        in determining where and how your data is transferred, stored, and
        processed.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        In compliance with the DPDPA, we will not transfer Personal Data to any
        country or jurisdiction specifically restricted by the Government of
        India. For all other international data transfers, we ensure that
        appropriate technical, contractual, and organizational safeguards are in
        place to maintain an adequate level of protection for your Personal
        Data, consistent with this Privacy Policy and applicable law.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        By using our Services, you acknowledge and agree that such transfers may
        occur as part of our service provision, and you consent to our handling
        of your data in this manner. We are not liable for data protection
        practices of third parties or foreign jurisdictions but remain committed
        to implementing reasonable measures to protect your data during transfer
        and processing.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        9. Updates to This Policy
      </Typography>
      <Typography variant="homePageBody" paragraph>
        We may update this Privacy Policy from time to time to reflect changes
        in our practices, technology, legal requirements, or other factors. When
        we do, we will post the updated policy on this page and update the{' '}
        <b>"Effective Date" at the Bottom.</b>
      </Typography>
      <Typography variant="homePageBody" paragraph>
        If we make material changes that significantly alter your privacy
        rights, we will provide more prominent notice, such as by sending an
        email notification or by displaying a notice on our Services prior to
        the change becoming effective. We encourage you to review this Privacy
        Policy periodically to stay informed about how we are protecting your
        information.  
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        10. How to Contact Us
      </Typography>
      <Typography variant="homePageBody" paragraph>
        If you have any questions, comments, or concerns about this Privacy
        Policy or our data practices, or if you wish to exercise your rights,
        please do not hesitate to contact us.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        <b>KaliyugAi Astrotech Pvt Ltd.</b>
        <br />
        India/New Delhi
      </Typography>
      <Typography variant="homePageBody" paragraph>
        For general privacy inquiries, please
        <br />
        Email us at: <b>support@kaliyugai.com</b>
      </Typography>
      <Typography
        variant="homePageBody"
        paragraph
        sx={{ marginBottom: '243px' }}
      >
        Privacy Policy Effective Date
        <br />
        From: <b>10-September-2025</b>
      </Typography>
    </Box>
  );
};

export default PrivacyPolicy;
